import { GradientPerFiscalite } from '@ui/gradient'
import clsx from 'clsx/lite'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { gsap } from 'gsap'
import React, { type FC, useState } from 'react'

import { type PerExampleId, perExamplesData } from './per-exemple-data'

const PerExempleFiscalite: FC = () => {
  const [activeTab, setActiveTab] = useState<PerExampleId>('salary')
  const activeTabData = perExamplesData.find((e) => e.id === activeTab)

  const profileImages = useStaticQuery<Queries.PerExamplesImagesQuery>(graphql`
    query PerExamplesImages {
      nonSalaryImage: contentfulAsset(
        contentful_id: { eq: "tiKPfgoiECSNCD898Q6CL" }
        metadata: {
          tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
        }
      ) {
        ...ContentfulDefaultAsset
      }
      salaryImage: contentfulAsset(
        contentful_id: { eq: "4zFDkOg39Ndkgkgcaoyd8o" }
        metadata: {
          tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
        }
      ) {
        ...ContentfulDefaultAsset
      }
    }
  `)

  const handleExample = (id: PerExampleId) => () => {
    gsap.to('.opacity-transition', {
      opacity: 0,
      duration: 0.2,
      onComplete: () => {
        setActiveTab(id)
        gsap.to('.opacity-transition', {
          opacity: 1,
          duration: 0.7,
        })
      },
    })
  }

  return (
    <div className="m-auto max-w-4xl tablet:px-5">
      <div className="mb-12 flex gap-1 rounded-md border border-solid border-grey-200 bg-slate-50 p-1 tablet:mx-auto tablet:w-[355px]">
        {perExamplesData.map(({ id, tabName }) => (
          <button
            type="button"
            className={clsx(
              'flex-1 rounded-md py-1',
              id === activeTab
                ? 'bg-background-default font-medium drop-shadow'
                : ''
            )}
            onClick={handleExample(id)}
          >
            {tabName}
          </button>
        ))}
      </div>
      <div className="flex flex-col rounded-[20px] border border-grey-100 drop-shadow-none tablet:flex-row tablet:border-solid tablet:shadow-md">
        <div className="relative mx-[-16px] flex-none overflow-hidden px-4 pb-8 tablet:mx-0 tablet:max-w-[285px] tablet:rounded-l-[0.625rem] tablet:p-8 tablet:pb-8 tablet:pr-6">
          <GradientPerFiscalite />
          <GatsbyImage
            alt={profileImages[activeTabData.image].description}
            className="opacity-transition mb-8 h-20 w-20 rounded-full tablet:h-16 tablet:w-16"
            image={getImage(profileImages[activeTabData.image])}
            objectFit="cover"
          />
          <p className="opacity-transition z-50 pb-2 text-110 font-medium">
            {activeTabData.title}
          </p>
          <p className="opacity-transition z-50 text-085 leading-alternate tablet:max-w-xs">
            {activeTabData.description}
          </p>
        </div>
        <div className="flex-1 pt-0 tablet:p-8 tablet:pr-12 tablet:pt-8">
          <div className="tablet:border-t-none flex items-center justify-between border-b border-t border-solid border-grey-100 py-5 pb-5 tablet:pt-0">
            <div>
              <p className="opacity-transition text-085">
                {activeTab === 'non-salary'
                  ? 'Bénéfices imposables'
                  : 'Revenu brut annuel'}
              </p>
              <p className="opacity-transition text-085">
                {activeTab === 'non-salary' ? '' : '(stable depuis 5 ans)'}
              </p>
            </div>
            <p className="opacity-transition text-085 font-medium tablet:text-100">
              {activeTabData.grossAnnualIncome.toLocaleString()} €
            </p>
          </div>
          <div className="flex items-center justify-between border-b border-solid border-grey-100 py-5">
            <p className="opacity-transition text-085">
              Plafond de déduction maximum*
            </p>
            <p className="opacity-transition text-085 font-medium tablet:text-100">
              {activeTabData.maximumDeductionlimit.toLocaleString()} €
            </p>
          </div>
          <div className="flex items-center justify-between border-b border-solid border-grey-100 py-5">
            <div>
              <p className="opacity-transition text-085">
                Versements sur Fortuneo PER
              </p>
              <p className="opacity-transition text-085">
                {activeTab === 'non-salary'
                  ? '(déductibles des bénéfices imposables)'
                  : '(déductibles du revenu net global)'}
              </p>
            </div>
            <p className="opacity-transition text-085 font-medium tablet:text-100">
              {activeTabData.FortuneoPrice.toLocaleString()} €
            </p>
          </div>
          <div className="flex items-center justify-between border-b border-solid border-grey-100 py-5">
            <p className="opacity-transition text-085">
              Taux marginal d’imposition (TMI)
            </p>
            <p className="opacity-transition text-085 font-medium tablet:text-100">
              {activeTabData.marginalTaxRate} %
            </p>
          </div>
          <div className="tablet:border-b-none flex items-center justify-between border-b border-solid border-grey-100 py-5 tablet:pb-0">
            <p className="opacity-transition text-085 font-medium">
              Avantage fiscal
            </p>
            <p className="opacity-transition text-150 font-medium text-green-700">
              {activeTabData.taxBenefits.toLocaleString()} €
            </p>
          </div>
        </div>
      </div>
      <p className="opacity-transition mt-6 text-085 tablet:px-0">
        {activeTabData.legalNotice}
      </p>
    </div>
  )
}

export default PerExempleFiscalite
